import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * CountUp — counter for the numbers lessons: lesson 11 counts one to ten,
 * lesson 31 carries on from eleven to nineteen.
 *
 * One card per counted noun (books, cars). Tapping + adds one item to the
 * pile: the icon row grows, the Arabic-Indic digit ticks up, and the phrase
 * morphs — كِتَابٌ ← كِتَابَانِ ← ثَلَاثَةُ كُتُبٍ… — with audio on every
 * step. From three, the number word is highlighted so the ة flip is visible
 * (masculine noun → number with ة, feminine noun → without). Completes when
 * every counter has been pushed to its last form (ten).
 */
function ItemIcon({ icon }) {
  const stroke = { stroke: "var(--gold-dim)", strokeWidth: 2, fill: "none" };
  if (icon === "car") {
    return (
      <svg viewBox="0 0 32 20" className="w-7 h-4 shrink-0" aria-hidden="true">
        <path d="M4 13 l2.5-6 a2 2 0 0 1 1.9-1.3 h15.2 a2 2 0 0 1 1.9 1.3 L28 13 v3 h-24 z" {...stroke} strokeLinejoin="round" />
        <circle cx="9.5" cy="16" r="2.4" fill="var(--tag-violet)" />
        <circle cx="22.5" cy="16" r="2.4" fill="var(--tag-violet)" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 24" className="w-4 h-5 shrink-0" aria-hidden="true">
      <rect x="3" y="3" width="14" height="18" rx="2" {...stroke} />
      <path d="M7 3 v18" {...stroke} />
    </svg>
  );
}

function MorphedPhrase({ form }) {
  // From three up the phrase is "number noun" — color the number so the
  // presence (or absence) of its ة is easy to spot across the two cards.
  // Lesson 11's numbers are one word each; lesson 31's teens are two
  // (ثَلَاثَةَ عَشَرَ), and only one of those halves wears the ة, so the whole
  // number has to be painted together. Rows name it in number_ar; without one
  // we fall back to the first word.
  const head = form.number_ar || (form.count >= 3 ? form.ar.split(" ")[0] : "");
  if (!head || !form.ar.startsWith(head)) return form.ar;
  return (
    <>
      <span style={{ color: "var(--gold-bright)" }}>{head}</span>
      {form.ar.slice(head.length)}
    </>
  );
}

export default function CountUp({ data, onComplete, audio }) {
  const items = data.items;
  const [counts, setCounts] = useState(() => Object.fromEntries(items.map((i) => [i.id, 1])));

  const doneCount = items.filter((i) => counts[i.id] >= i.forms.length).length;
  const done = doneCount >= items.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleAdd = (item) => {
    const next = counts[item.id] + 1;
    if (next > item.forms.length) return;
    setCounts((prev) => ({ ...prev, [item.id]: next }));
    if (audio) audio.play(item.forms[next - 1].ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{doneCount}/{items.length} {data.progress_label || "counted to ten"}</ProgressPill>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((item) => {
          const form = item.forms[counts[item.id] - 1];
          const atMax = counts[item.id] >= item.forms.length;
          return (
            <div
              key={item.id}
              className="rounded-xl px-4 py-4 flex flex-col items-center gap-3 text-center"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <div className="flex items-center justify-between w-full">
                <span dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.4]" style={{ color: "var(--gold-bright)" }}>
                  {form.digit}
                </span>
                <button
                  type="button"
                  onClick={() => handleAdd(item)}
                  disabled={atMax}
                  aria-label={`Add one more to the ${item.noun_en}`}
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full text-2xl font-semibold transition-all ${
                    atMax ? "cursor-default opacity-40" : "hover:brightness-125 animate-pulse cursor-pointer"
                  }`}
                  style={{ background: "var(--gold-bg)", color: "var(--gold-bright)", border: "1px dashed var(--gold-dim)" }}
                >
                  +
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-1.5 min-h-[3rem] max-w-[16rem]">
                {Array.from({ length: form.count }).map((_, i) => (
                  <ItemIcon key={i} icon={item.icon} />
                ))}
              </div>

              <p className="m-0 flex items-center justify-center gap-3">
                <span dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7]" style={{ color: "var(--parchment)" }}>
                  <MorphedPhrase form={form} />
                </span>
                {audio && (
                  <PlayButton text={form.ar} playingText={audio.playingText} onPlay={audio.play} />
                )}
              </p>
              <p className="text-sm m-0" style={{ color: "var(--parchment-dim)" }}>{form.en}</p>
              {form.note && (
                <p className="text-xs m-0 leading-relaxed" style={{ color: "var(--gold-dim)" }}>
                  <MixedText text={form.note} />
                </p>
              )}
            </div>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
